import React, { useRef, useState } from 'react';
import SectionWrapper from '../UI/SectionWrapper';
import { Check } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    landSize: '',
    message: '',
    newsletter: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      // Use environment variable or relative path for production
      const API_URL = process.env.REACT_APP_API_URL || '';
      const endpoint = `${API_URL}/api/contact`;
      
      // Call backend API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          location: '',
          landSize: '',
          message: '',
          newsletter: false
        });
      } else {
        alert(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert("Failed to send message. Please check if the backend server is running and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SectionWrapper id="contact" className="bg-[#1B3022] text-earthy-sand py-20"> 
    {/* Using a specific dark green hex if forest-green is too light, or we can use bg-forest-green text-white */}
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-3xl md:text-5xl font-serif text-center mb-8 tracking-wide">
          Contact Me
        </h2>
        
        {!isSuccess ? (
          <>
            <p className="text-center mb-16">Contact me if you would like to know more about having your land healed. Healing can be done remotely via an emailed map. Prices are found here.
            Or, if you are interested in collaborating on a project. Please Reach out.</p>

            <form onSubmit={handleSubmit} className="space-y-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                {/* First Name */}
                <div className="relative group">
                  <label className="block text-sm text-white/70 mb-1">First Name <span className="text-red-400">*</span></label>
                  <input 
                    type="text" 
                    name="firstName" 
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Jane"
                    className={`w-full bg-transparent border-b py-2 text-lg focus:outline-none transition-colors placeholder-white/30 ${errors.firstName ? 'border-red-400' : 'border-white/30 focus:border-white'}`}
                  />
                  {errors.firstName && <span className="absolute -bottom-6 left-0 text-red-400 text-xs">{errors.firstName}</span>}
                </div>

                {/* Last Name */}
                <div className="relative group">
                  <label className="block text-sm text-white/70 mb-1">Last Name <span className="text-red-400">*</span></label>
                  <input 
                    type="text" 
                    name="lastName" 
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    className={`w-full bg-transparent border-b py-2 text-lg focus:outline-none transition-colors placeholder-white/30 ${errors.lastName ? 'border-red-400' : 'border-white/30 focus:border-white'}`}
                  />
                  {errors.lastName && <span className="absolute -bottom-6 left-0 text-red-400 text-xs">{errors.lastName}</span>}
                </div>

                {/* Email */}
                <div className="relative group">
                  <label className="block text-sm text-white/70 mb-1">Email Address <span className="text-red-400">*</span></label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    className={`w-full bg-transparent border-b py-2 text-lg focus:outline-none transition-colors placeholder-white/30 ${errors.email ? 'border-red-400' : 'border-white/30 focus:border-white'}`}
                  />
                  {errors.email && <span className="absolute -bottom-6 left-0 text-red-400 text-xs">{errors.email}</span>}
                </div>

                {/* Phone */}
                <div className="relative group">
                  <label className="block text-sm text-white/70 mb-1">Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+61 400 000 000"
                    className="w-full bg-transparent border-b border-white/30 py-2 text-lg focus:outline-none focus:border-white transition-colors placeholder-white/30"
                  />
                </div>

                {/* Location */}
                <div className="relative group">
                  <label className="block text-sm text-white/70 mb-1">Location/Suburb</label>
                  <input 
                    type="text" 
                    name="location" 
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Brisbane"
                    className="w-full bg-transparent border-b border-white/30 py-2 text-lg focus:outline-none focus:border-white transition-colors placeholder-white/30"
                  />
                </div>

                {/* Land Size */}
                <div className="relative group">
                  <label className="block text-sm text-white/70 mb-1">Land Size</label>
                  <input 
                    type="text" 
                    name="landSize" 
                    value={formData.landSize}
                    onChange={handleChange}
                    placeholder="e.g. 800sqm"
                    className="w-full bg-transparent border-b border-white/30 py-2 text-lg focus:outline-none focus:border-white transition-colors placeholder-white/30"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="relative group mt-10">
                <label className="block text-sm text-white/70 mb-2">Message <span className="text-red-400">*</span></label>
                <textarea 
                  name="message" 
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can I help you?"
                  className={`w-full bg-white/10 border-none rounded-sm p-4 text-lg focus:outline-none focus:ring-1 transition-colors text-white resize-none ${errors.message ? 'ring-1 ring-red-400' : 'focus:ring-white/50'}`}
                ></textarea>
                {errors.message && <span className="absolute -bottom-6 left-0 text-red-400 text-xs">{errors.message}</span>}
              </div>

              <div className="flex flex-col md:flex-row items-center justify-center pt-6">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="px-12 py-4 border border-teal-500/50 text-teal-300 hover:bg-teal-500 hover:text-white transition-all duration-300 rounded-sm font-medium tracking-widest text-sm uppercase flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-teal-300/30 border-t-teal-300 rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : 'Send Message'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in zoom-in duration-500">
            <div className="w-24 h-24 bg-teal-500/20 rounded-full flex items-center justify-center text-teal-400 mb-8 border border-teal-500/30">
              <Check size={48} strokeWidth={3} />
            </div>
            <h3 className="text-4xl font-serif text-white mb-4">Message Sent!</h3>
            <p className="text-white/70 max-w-md mx-auto mb-10 text-lg leading-relaxed">
              Thank you for reaching out. I have received your inquiry and will get back to you personally within 24-48 hours.
            </p>
            <button 
              onClick={() => setIsSuccess(false)}
              className="px-8 py-2 border border-white/20 text-white/60 hover:text-white hover:border-white/50 transition-colors rounded-sm text-sm uppercase tracking-widest"
            >
              Send Another Message
            </button>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export default Contact;
